// File: frontend/src/pages/customTopic/CustomTopicOwnEntries.tsx

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RiInformationLine } from "react-icons/ri";
import {
  fetchOwnCustomTopicEntries,
  OwnTopicEntries,
} from "../../services/customTopicEntryService";
import { formatDateTime } from "../../utils/dateUtils";
import "./CustomTopicOwnEntries.css";

interface CustomTopicOwnEntriesProps {
  // Custom topic whose own-entry history is shown
  topicId: number;
  // Any change re-fetches the list (after the viewer posted or deleted an
  // entry, or after the page was reloaded from the left pane)
  refreshKey: string;
}

/**
 * Notice rendered under the entry form of a custom topic (gündem) page.
 * Custom topics only — the other domains have no such rule.
 *
 * Lists the viewer's earlier entries in this topic as "#id (date)"
 * permalinks, newest first. The backend caps the list; when entries were left
 * out, a closing "ve N tanım daha" line tells how many.
 *
 * The consecutive-entry rule is NOT announced here: a permanent warning under
 * the form was noise. When the viewer tries to post a second entry in a row,
 * the backend rejects it (CUSTOM_TOPIC_CONSECUTIVE_ENTRY) and EntryPostForm
 * shows the translated error above the textarea.
 *
 * Renders nothing when the viewer has never written in the topic. A failed
 * request is only logged, the list is purely informational.
 */
export const CustomTopicOwnEntries: React.FC<CustomTopicOwnEntriesProps> = ({ topicId, refreshKey }) => {
  const { t } = useTranslation();
  const [data, setData] = useState<OwnTopicEntries | null>(null);

  useEffect(() => {
    // Ignore a response that arrives after the topic changed
    let cancelled = false;
    fetchOwnCustomTopicEntries(topicId)
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch((err) => {
        console.error("Own custom topic entries could not be loaded:", err);
        if (!cancelled) setData(null);
      });
    return () => { cancelled = true; };
  }, [topicId, refreshKey]);

  if (!data || data.entries.length === 0) return null;

  // Entries not listed because of the backend cap
  const hiddenCount = Math.max(0, data.totalCount - data.entries.length);

  return (
    <div className="ct-own-entries">
      <div className="ct-own-entries__box">
        <RiInformationLine className="ct-own-entries__icon" aria-hidden="true" />
        <div className="ct-own-entries__body">
          <span className="ct-own-entries__title">{t("custom_topic_own_entries_title")}</span>
          <ul className="ct-own-entries__list">
            {data.entries.map((entry) => (
              <li key={entry.id} className="ct-own-entries__item">
                <Link to={`/entry/${entry.id}`} className="ct-own-entries__link">
                  #{entry.id}
                </Link>
                <span className="ct-own-entries__date">({formatDateTime(entry.createdAt)})</span>
              </li>
            ))}
          </ul>
          {hiddenCount > 0 && (
            <span className="ct-own-entries__more">
              {t("custom_topic_own_entries_more", { count: hiddenCount })}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomTopicOwnEntries;